/**
 * Step 3.5 -- per-region recolouring of the hero atlas.
 *
 * What the art actually provides, measured in step 3.4 rather than assumed:
 *
 * * **Colourway** -- a whole replacement atlas. Thirteen ship, not the four the asset
 *   document describes, and swapping one recolours 70-100% of a part's texels.
 * * **Region tint** -- the four mask textures each carry three channels, and a channel
 *   **at or below 0.5 selects a region**. Twelve regions in total, of which ten are
 *   used by real parts: skin, a second skin tone, hair, leather, two cloths, two
 *   metals, gold trim and the heraldry field.
 *
 * The regions are *palette swatches*, not painted areas -- each covers well under 1%
 * of the atlas, yet one swatch is 100% of a hand's UVs. That single fact decides the
 * implementation.
 *
 * **Why baking, not a shader.** The asset document calls the tint shader "the one
 * genuinely new piece of rendering work". With regions this small it is the wrong
 * tool: baking a tinted copy of the atlas touches a few thousand pixels per region,
 * runs once per appearance rather than once per fragment, and leaves the character on
 * an ordinary material -- so Babylon's skinning, lighting and shadows keep working
 * with no custom vertex path to maintain. A custom `ShaderMaterial` would have to
 * re-implement skinned vertex transformation to gain nothing here. The cost is that a
 * tint change re-bakes rather than updating a uniform, which suits a character
 * creator and is irrelevant in combat, where tints do not change.
 */
import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Constants } from '@babylonjs/core/Engines/constants';
import type { Scene } from '@babylonjs/core/scene';

import '@babylonjs/core/Materials/standardMaterial';

/** A mask channel at or below this selects its region. Matches the pipeline. */
export const REGION_THRESHOLD = 0.5 * 255;

export interface TintRegionSource {
  /** `Mask_03.r` -- matches the catalogue's `tintRegions[].id`. */
  id: string;
  /** URL of the mask texture. */
  mask: string;
  channel: 'R' | 'G' | 'B';
}

/** Region id -> `#rrggbb`. Absent regions keep the atlas colour. */
export type TintSelection = Record<string, string>;

interface RegionIndex {
  id: string;
  /** Offsets into the RGBA pixel array, one per selected pixel. */
  pixels: Int32Array;
}

export interface TintPalette {
  width: number;
  height: number;
  /** The untinted colourway, RGBA. */
  base: Uint8ClampedArray;
  regions: RegionIndex[];
  colourway: string;
}

async function readPixels(url: string): Promise<{ data: Uint8ClampedArray; width: number; height: number }> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error(`could not load ${url}`));
    image.src = url;
  });

  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('no 2d context for atlas baking');
  context.drawImage(image, 0, 0);
  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  return { data, width: canvas.width, height: canvas.height };
}

/**
 * Loads a colourway and resolves every region to a pixel list.
 *
 * Done once per colourway and cached: the mask scan is a single pass over a megapixel,
 * and every later re-bake only touches the pixels a region actually owns.
 */
export async function loadTintPalette(
  colourwayUrl: string,
  regions: TintRegionSource[],
): Promise<TintPalette> {
  const base = await readPixels(colourwayUrl);

  // Masks are shared between regions, so each file is read at most once.
  const maskCache = new Map<string, Uint8ClampedArray>();
  const resolved: RegionIndex[] = [];

  for (const region of regions) {
    let mask = maskCache.get(region.mask);
    if (!mask) {
      const loaded = await readPixels(region.mask);
      if (loaded.width !== base.width || loaded.height !== base.height) {
        throw new Error(`${region.mask}: mask size does not match the atlas`);
      }
      mask = loaded.data;
      maskCache.set(region.mask, mask);
    }

    const offset = region.channel === 'R' ? 0 : region.channel === 'G' ? 1 : 2;
    const found: number[] = [];
    for (let i = 0; i < mask.length; i += 4) {
      if ((mask[i + offset] ?? 255) <= REGION_THRESHOLD) found.push(i);
    }
    resolved.push({ id: region.id, pixels: Int32Array.from(found) });
  }

  return {
    width: base.width,
    height: base.height,
    base: base.data,
    regions: resolved,
    colourway: colourwayUrl,
  };
}

function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) return null;
  const value = Number.parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** Multiplies each selected region by its tint and returns a fresh RGBA buffer. */
export function bakeAtlas(palette: TintPalette, tints: TintSelection): Uint8Array {
  const out = new Uint8Array(palette.base.length);
  out.set(palette.base);

  for (const region of palette.regions) {
    const hex = tints[region.id];
    if (!hex) continue;
    const rgb = parseHex(hex);
    if (!rgb) continue;

    // Multiply rather than replace, so the swatch keeps whatever shading it had.
    const [tr, tg, tb] = [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
    for (const i of region.pixels) {
      out[i] = Math.min(255, (out[i] ?? 0) * tr);
      out[i + 1] = Math.min(255, (out[i + 1] ?? 0) * tg);
      out[i + 2] = Math.min(255, (out[i + 2] ?? 0) * tb);
    }
  }
  return out;
}

export interface HeroTintMaterial {
  material: StandardMaterial;
  /** Re-bakes and swaps the texture in place; the material identity is stable. */
  setTints(tints: TintSelection): void;
  dispose(): void;
}

/**
 * One material per character, carrying one baked texture.
 *
 * That is the property the merged body depends on: every part binds to the same
 * atlas, so an assembled character stays a single draw call however many slots it
 * fills.
 */
export function createHeroTintMaterial(
  scene: Scene,
  palette: TintPalette,
  tints: TintSelection = {},
): HeroTintMaterial {
  const material = new StandardMaterial('heroTint', scene);
  // The POLYGON look is flat; a specular highlight reads as a rendering error on it.
  material.specularColor = new Color3(0, 0, 0);

  let texture: RawTexture | null = null;

  const apply = (selection: TintSelection): void => {
    const baked = bakeAtlas(palette, selection);
    const next = new RawTexture(
      baked,
      palette.width,
      palette.height,
      Constants.TEXTUREFORMAT_RGBA,
      scene,
      true,
      false,
      Constants.TEXTURE_TRILINEAR_SAMPLINGMODE,
    );
    next.name = 'heroAtlas';
    material.diffuseTexture = next;
    texture?.dispose();
    texture = next;
  };

  apply(tints);

  return {
    material,
    setTints: apply,
    dispose() {
      texture?.dispose();
      material.dispose();
    },
  };
}
